import React from 'react';
import Select from 'react-select';
import { cn } from '../utils/cn';

/**
 * ReactSelect Component - Atom
 * Modern select dropdown using react-select
 * 
 * @param {Object} props
 * @param {Array} props.options - Options array [{value, label}]
 * @param {Object|Array} props.value - Selected value(s)
 * @param {Function} props.onChange - Change handler
 * @param {boolean} props.isMulti - Multi-select mode
 * @param {boolean} props.isDisabled - Disabled state
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.size - 'sm' | 'md' | 'lg'
 * @param {string} props.variant - 'default' | 'error' | 'success'
 */
const ReactSelect = React.forwardRef(
  (
    {
      options = [],
      value,
      onChange,
      isMulti = false,
      isDisabled = false,
      placeholder = 'Select...',
      size = 'md',
      variant = 'default',
      className = '',
      ...props
    },
    ref
  ) => {
    const getSizeStyles = () => {
      switch (size) {
        case 'sm':
          return {
            control: 'min-h-[36px]',
            input: 'text-sm',
            placeholder: 'text-sm',
            singleValue: 'text-sm',
            multiValue: 'text-sm',
          };
        case 'lg':
          return {
            control: 'min-h-[48px]',
            input: 'text-lg',
            placeholder: 'text-lg',
            singleValue: 'text-lg',
            multiValue: 'text-lg',
          };
        default:
          return {
            control: 'min-h-[42px]',
            input: 'text-base',
            placeholder: 'text-base',
            singleValue: 'text-base',
            multiValue: 'text-base',
          };
      }
    };

    const sizeStyles = getSizeStyles();

    const getVariantColors = () => {
      switch (variant) {
        case 'error':
          return {
            border: 'border-error-400 dark:border-error-600',
            bg: 'bg-error-50 dark:bg-error-900/30',
            text: 'text-error-900 dark:text-error-50',
            focus: 'focus:border-error-500 dark:focus:border-error-500',
            ring: 'focus:ring-error-500/50 dark:focus:ring-error-500/50',
          };
        case 'success':
          return {
            border: 'border-success-400 dark:border-success-600',
            bg: 'bg-success-50 dark:bg-success-900/30',
            text: 'text-success-900 dark:text-success-50',
            focus: 'focus:border-success-500 dark:focus:border-success-500',
            ring: 'focus:ring-success-500/50 dark:focus:ring-success-500/50',
          };
        default:
          return {
            border: 'border-primary-200 dark:border-primary-700',
            bg: 'bg-white dark:bg-primary-800',
            text: 'text-primary-900 dark:text-primary-50',
            focus: 'focus:border-secondary-500 dark:focus:border-secondary-400',
            ring: 'focus:ring-secondary-500/50 dark:focus:ring-secondary-400/50',
          };
      }
    };

    const colors = getVariantColors();

    const customStyles = {
      control: (base, state) => ({
        ...base,
        minHeight: sizeStyles.control.replace('min-h-[', '').replace(']', ''),
        borderRadius: '0.75rem',
        borderWidth: '2px',
        borderColor: state.isFocused
          ? (variant === 'error' ? '#ef4444' : variant === 'success' ? '#22c55e' : '#0ea5e9')
          : state.isDisabled
          ? '#cbd5e1'
          : variant === 'error'
          ? '#f87171'
          : variant === 'success'
          ? '#4ade80'
          : '#cbd5e1',
        backgroundColor: state.isDisabled
          ? '#f1f5f9'
          : variant === 'error'
          ? '#fef2f2'
          : variant === 'success'
          ? '#f0fdf4'
          : '#ffffff',
        boxShadow: state.isFocused
          ? `0 0 0 2px ${variant === 'error' ? 'rgba(239, 68, 68, 0.5)' : variant === 'success' ? 'rgba(34, 197, 94, 0.5)' : 'rgba(14, 165, 233, 0.5)'}`
          : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        '&:hover': {
          borderColor: state.isFocused
            ? (variant === 'error' ? '#ef4444' : variant === 'success' ? '#22c55e' : '#0ea5e9')
            : '#94a3b8',
          boxShadow: state.isFocused
            ? `0 0 0 2px ${variant === 'error' ? 'rgba(239, 68, 68, 0.5)' : variant === 'success' ? 'rgba(34, 197, 94, 0.5)' : 'rgba(14, 165, 233, 0.5)'}`
            : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        },
      }),
      menu: (base) => ({
        ...base,
        borderRadius: '0.75rem',
        borderWidth: '2px',
        borderColor: '#cbd5e1',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        backgroundColor: '#ffffff',
        zIndex: 9999,
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? '#0ea5e9'
          : state.isFocused
          ? '#e0f2fe'
          : 'transparent',
        color: state.isSelected ? '#ffffff' : '#1e293b',
        '&:active': {
          backgroundColor: '#0ea5e9',
          color: '#ffffff',
        },
      }),
      placeholder: (base) => ({
        ...base,
        fontSize: sizeStyles.placeholder.replace('text-', ''),
        color: '#94a3b8',
      }),
      singleValue: (base) => ({
        ...base,
        fontSize: sizeStyles.singleValue.replace('text-', ''),
        color: '#1e293b',
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: '#e0f2fe',
        borderRadius: '0.5rem',
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: '#0c4a6e',
        fontSize: sizeStyles.multiValue.replace('text-', ''),
      }),
      multiValueRemove: (base) => ({
        ...base,
        color: '#0c4a6e',
        '&:hover': {
          backgroundColor: '#bae6fd',
          color: '#0c4a6e',
        },
      }),
    };

    // Dark mode styles
    const darkModeStyles = {
      control: (base, state) => ({
        ...base,
        backgroundColor: state.isDisabled
          ? '#1e293b'
          : variant === 'error'
          ? 'rgba(127, 29, 29, 0.3)'
          : variant === 'success'
          ? 'rgba(20, 83, 45, 0.3)'
          : '#1e293b',
        borderColor: state.isFocused
          ? (variant === 'error' ? '#ef4444' : variant === 'success' ? '#22c55e' : '#38bdf8')
          : '#334155',
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: '#1e293b',
        borderColor: '#334155',
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? '#0ea5e9'
          : state.isFocused
          ? '#1e3a5f'
          : 'transparent',
        color: state.isSelected ? '#ffffff' : '#e2e8f0',
      }),
      singleValue: (base) => ({
        ...base,
        color: '#e2e8f0',
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: '#1e3a5f',
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: '#e2e8f0',
      }),
    };

    // Detect dark mode
    const isDarkMode = typeof window !== 'undefined' && document.documentElement.classList.contains('dark');

    return (
      <div className={cn('w-full', className)}>
        <Select
          ref={ref}
          options={options}
          value={value}
          onChange={onChange}
          isMulti={isMulti}
          isDisabled={isDisabled}
          placeholder={placeholder}
          styles={{
            ...customStyles,
            ...(isDarkMode ? darkModeStyles : {}),
          }}
          classNamePrefix="react-select"
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary: '#0ea5e9',
              primary75: '#38bdf8',
              primary50: '#7dd3fc',
              primary25: '#bae6fd',
            },
          })}
          {...props}
        />
      </div>
    );
  }
);

ReactSelect.displayName = 'ReactSelect';

export default ReactSelect;
